import {faCrown, faSignOutAlt} from '@fortawesome/free-solid-svg-icons'
import {identity} from 'ramda'
import * as React from 'react'
import type {TWaitingRoomResponse} from '@kapitalist/contract/endpoints'
import {
  MAX_NUMBER_OF_PLAYERS,
  MIN_NUMBER_OF_PLAYERS,
} from '@kapitalist/contract/ruleset'
import type {TPlayerId} from '@kapitalist/contract/types'
import type {TJoinGameResponse} from '~/api/endpoints/game/PreGame/JoinGame'
import type {TStartGameResponse} from '~/api/endpoints/game/PreGame/StartGame'
import Gravatar from '~/components/Gravatar/stateless'
import type {TConstraint} from '~/components/ProvideForm/utils'
import {
  AdvancedConstraint,
  floatRangeConstraint,
} from '~/components/ProvideForm/utils'
import {SingleButtonForm} from '~/components/SingleButtonForm'
import DSText, {DSTextDiv} from '~/design-system/DSText'
import {DSLinkTextDiv} from '~/design-system/Link'
import type AuthenticatedPlayer from '~/models/app/AuthenticatedPlayer'
import {copyTextToClipboard} from '~/utils/clipboard'
import {HoverableFontAwesomeIconDiv} from '~/utils/FontAwesome/HoverableFontAwesomeIcon'
import styles from './styles.module.css'

interface IProps {
  data: TWaitingRoomResponse

  joinGame: () => Promise<TJoinGameResponse>
  joinGameSuccess: () => void

  startGame: () => Promise<TStartGameResponse>
  kickPlayer: (playerId: TPlayerId) => Promise<unknown>
  gameStarted: () => void
  authenticatedPlayer: AuthenticatedPlayer
}

const rangeConstraint = (playerCount: number) => {
  return new AdvancedConstraint<any>(() => {
    return floatRangeConstraint(
      MIN_NUMBER_OF_PLAYERS,
      MAX_NUMBER_OF_PLAYERS,
      '[]',
      ({value}) =>
        value < MIN_NUMBER_OF_PLAYERS ? 'Not enough players yet' : 'Too many players'
    ).test('' + playerCount)
  })
}

const joinableConstraints = (data: TWaitingRoomResponse): TConstraint<any>[] => [
  new AdvancedConstraint<any>(() => {
    return floatRangeConstraint(0, MAX_NUMBER_OF_PLAYERS, '[]').test(
      '' + data.players.length
    )
  }),
  {
    constraint: () => data.status === 'waiting',
    message: () => 'The game is no longer joinable',
  },
]

export default class WaitingRoomUI extends React.Component<IProps> {
  render() {
    const {hostPlayerId, players} = this.props.data
    const isHost = hostPlayerId === this.props.authenticatedPlayer.id
    const hasntJoinedYet = !players.find(
      p => p.playerId === this.props.authenticatedPlayer.id
    )

    return (
      <div className={styles.waitingRoomWrapper}>
        <DSTextDiv size="body-17">Players:</DSTextDiv>
        <div className={styles.playerList}>
          {players.map(p => (
            <div key={p.playerId} className={styles.playerRow}>
              {p.playerId === hostPlayerId && (
                <HoverableFontAwesomeIconDiv
                  className={styles.hostIcon}
                  icon={{
                    icon: faCrown,
                    style: {width: '14px', height: '14px'},
                  }}
                >
                  {identity}
                </HoverableFontAwesomeIconDiv>
              )}
              {(isHost
                ? p.playerId !== hostPlayerId
                : p.playerId === this.props.authenticatedPlayer.id) && (
                <HoverableFontAwesomeIconDiv
                  className={styles.kickPlayerIcon}
                  icon={{
                    icon: faSignOutAlt,
                    color: 'grey',
                    style: {width: '13px', height: '13px'},
                  }}
                  hoverIcon={{color: 'red-dark'}}
                  onClick={() =>
                    this.props.kickPlayer(p.playerId).catch(() => undefined)
                  }
                >
                  {identity}
                </HoverableFontAwesomeIconDiv>
              )}
              <Gravatar email={p.email} size={40} />
              <DSText tag="p" size="body-15-demi">
                {p.username}
              </DSText>
            </div>
          ))}
        </div>

        <DSLinkTextDiv
          underline
          size="body-15"
          onClick={() => copyTextToClipboard(window.location.href)}
        >
          Copy Invite URL
        </DSLinkTextDiv>
        {hasntJoinedYet && (
          <SingleButtonForm
            buttonStyle="primary"
            size="17px"
            onSubmit={() => this.props.joinGame()}
            submitSuccess={this.props.joinGameSuccess}
            peripheralConstraints={joinableConstraints(this.props.data)}
            classNames={{wrapper: styles.button}}
          >
            Join Game
          </SingleButtonForm>
        )}
        {isHost && (
          <SingleButtonForm
            buttonStyle="primary"
            size="17px"
            onSubmit={() => this.props.startGame()}
            submitSuccess={this.props.gameStarted}
            peripheralConstraints={[rangeConstraint(players.length)]}
            classNames={{wrapper: styles.button}}
          >
            Start Game
          </SingleButtonForm>
        )}
      </div>
    )
  }
}
